import { Vector2 } from '@seddi/math';
import PointerLock from 'pointer-lock';
import Touches from 'touches';

export type PointerFrame = {
  capture: (button: any) => void;
  primary: boolean;
  primaryDown: boolean;
  primaryPressed: boolean;
  primaryDrag: boolean;
  primaryDragDown: boolean;
  primaryDoubleDown: boolean;
  primaryUp: boolean;
  primaryDblUp: boolean;
  secondary: boolean;
  secondaryDown: boolean;
  secondaryDrag: boolean;
  secondaryDragDown: boolean;
  secondaryPressed: boolean;
  secondaryUp: boolean;
  tertiary: boolean;
  tertiaryDown: boolean;
  tertiaryDrag: boolean;
  tertiaryPressed: boolean;
  tertiaryDragDown: boolean;
  tertiaryUp: boolean;
  hover: boolean;
  locked: boolean;
  current: Vector2;
  movement: Vector2;
  normalized: Vector2;
  wheel: number;
  window: Vector2;
  wheelEvent?: WheelEvent;
  worldspace?: Vector2;
};

export class Pointer {
  dragBias: number;
  dblThreshold: number;
  primary: boolean;
  primaryDown: boolean;
  primaryPressed: boolean;
  primaryDrag: boolean;
  primaryDragDown: boolean;
  primaryDoubleDown: boolean;
  primaryUp: boolean;
  secondary: boolean;
  secondaryDown: boolean;
  secondaryDrag: boolean;
  secondaryDragDown: boolean;
  secondaryPressed: boolean;
  secondaryUp: boolean;
  tertiary: boolean;
  tertiaryDown: boolean;
  tertiaryDrag: boolean;
  tertiaryPressed: boolean;
  tertiaryDragDown: boolean;
  tertiaryUp: boolean;
  hover: boolean;
  locked: boolean;
  current: Vector2;
  movement: Vector2;
  normalized: Vector2;
  lastMovement: Vector2;
  size: Vector2;
  worldspace: Vector2;
  lastPrimary: number;
  dragStart: {
    primary: Vector2;
    secondary: Vector2;
    tertiary: Vector2;
  };
  wheel: number;
  window: Vector2;
  wheelEvent?: WheelEvent;
  touches: Touches;
  pointerlock: PointerLock;
  constructor({ canvas }: { canvas: HTMLCanvasElement }) {
    this.dblThreshold = 250;
    this.dragBias = 2;
    this.primary = false;
    this.primaryDown = false;
    this.primaryPressed = false;
    this.primaryDrag = false;
    this.primaryDragDown = false;
    this.primaryDoubleDown = false;
    this.primaryUp = false;
    this.secondary = false;
    this.secondaryDown = false;
    this.secondaryDrag = false;
    this.secondaryDragDown = false;
    this.secondaryPressed = false;
    this.secondaryUp = false;
    this.tertiary = false;
    this.tertiaryDown = false;
    this.tertiaryDrag = false;
    this.tertiaryPressed = false;
    this.tertiaryDragDown = false;
    this.tertiaryUp = false;
    this.hover = false;
    this.locked = false;
    this.lastPrimary = 0;
    this.dragStart = {
      primary: new Vector2(0, 0),
      secondary: new Vector2(0, 0),
      tertiary: new Vector2(0, 0),
    };
    this.current = new Vector2(0, 0);
    this.movement = new Vector2(0, 0);
    this.normalized = new Vector2(0, 0);
    this.lastMovement = new Vector2(0, 0);
    this.size = new Vector2(0, 0);
    this.wheel = 0;
    this.window = new Vector2(0, 0);
    this.onCapture = this.onCapture.bind(this);
    this.worldspace = new Vector2();

    this.onPointerEnter = this.onPointerEnter.bind(this);
    canvas.addEventListener('mouseenter', this.onPointerEnter, false);

    this.onPointerLeave = this.onPointerLeave.bind(this);
    canvas.addEventListener('mouseleave', this.onPointerLeave, false);

    this.touches = Touches(window, {
      filtered: true,
      preventSimulated: false,
      target: canvas,
    })
      .on('start', this.onPointerDown.bind(this))
      .on('move', this.onPointerMove.bind(this))
      .on('end', this.onPointerUp.bind(this));

    this.onPointerWheel = this.onPointerWheel.bind(this);
    window.addEventListener('wheel', this.onPointerWheel, { passive: false });
  }

  dispose() {
    const { pointerlock, touches } = this;
    if (pointerlock) {
      pointerlock.destroy();
    }
    touches.disable();
    window.removeEventListener('wheel', this.onPointerWheel);
  }

  onCapture(button: string) {
    this[`${button}`] = false;
    this[`${button}Drag`] = false;
    this[`${button}DragDown`] = false;
    this[`${button}Down`] = false;
    this[`${button}Up`] = false;
  }

  onPointerDown(e: PointerEvent, [x, y]: number[]) {
    const { button = 0 } = e;
    const { dragStart, hover, locked } = this;
    if (!hover || locked) {
      return;
    }

    switch (button) {
      case 0:
        this.primary = true;
        this.primaryDown = true;
        this.primaryPressed = true;
        this.primaryDoubleDown = e.detail == 2;
        dragStart.primary.set(x, y);
        break;
      case 2:
        this.secondary = true;
        this.secondaryDown = true;
        this.secondaryPressed = true;
        dragStart.secondary.set(x, y);
        break;
      case 1:
        this.tertiary = true;
        this.tertiaryDown = true;
        this.tertiaryPressed = true;
        dragStart.tertiary.set(x, y);
        break;
      default:
        break;
    }
    this.onPointerMove(e, [x, y]);
  }

  onPointerEnter() {
    this.hover = true;
  }

  onPointerLeave() {
    this.hover = false;
  }

  onPointerMove(e: PointerEvent, [x, y]: number[]) {
    const { dragBias } = this;
    const {
      current,
      normalized,
      size,
      dragStart,
      primary,
      primaryDrag,
      primaryDown,
      secondary,
      secondaryDrag,
      secondaryDown,
      tertiary,
      tertiaryDrag,
      tertiaryDown,
      window,
    } = this;
    current.x = x;
    current.y = y;
    normalized.x = (x / size.x) * 2 - 1;
    normalized.y = 1 - (y / size.y) * 2;
    window.x = e.clientX;
    window.y = e.clientY;
    if (primary && !primaryDrag && !primaryDown && dragStart.primary.distanceTo(current) >= dragBias) {
      this.primaryDrag = true;
      this.primaryDragDown = true;
    }
    if (secondary && !secondaryDrag && !secondaryDown && dragStart.secondary.distanceTo(current) >= dragBias) {
      this.secondaryDrag = true;
      this.secondaryDragDown = true;
    }
    if (tertiary && !tertiaryDrag && !tertiaryDown && dragStart.tertiary.distanceTo(current) >= dragBias) {
      this.tertiaryDrag = true;
      this.tertiaryDragDown = true;
    }
  }

  onPointerUp({ button = 0 }) {
    const { primary, secondary, tertiary } = this;
    switch (button) {
      case 0:
        if (primary) {
          this.primaryUp = true;
          this.primaryPressed = false;
        }
        this.primary = false;
        this.primaryDrag = false;
        break;
      case 2:
        if (secondary) {
          this.secondaryUp = true;
        }
        this.secondaryPressed = false;
        this.secondary = false;
        this.secondaryDrag = false;
        break;
      case 1:
        if (tertiary) {
          this.tertiaryUp = true;
          this.tertiaryPressed = false;
        }
        this.tertiary = false;
        this.tertiaryDrag = false;
        break;
      default:
        break;
    }
  }

  onPointerLock(movements: any) {
    const { lastMovement } = this;
    this.locked = true;
    lastMovement.set(0, 0);
    movements.on('data', ({ dx, dy }: { dx: number; dy: number }) =>
      lastMovement.set(lastMovement.x + dx, lastMovement.y + dy),
    );
    movements.once('close', () => {
      this.locked = false;
    });
  }

  onPointerWheel(e: WheelEvent) {
    // TODO It would be nice to use the last wheel event with wheel,
    // as it is more complex than just having deltaY. As there is
    // code working with this.wheel, it remains like this.
    this.wheel = e.deltaY;
    this.wheelEvent = e;
  }

  setSize(width: number, height: number) {
    this.size.set(width, height);
  }

  requestPointerLock() {
    const { touches } = this;
    if (!this.pointerlock) {
      this.pointerlock = PointerLock(touches.target);
      this.pointerlock.on('attain', this.onPointerLock.bind(this));
    }
    this.pointerlock.request();
  }

  getFrame() {
    const { current, lastMovement, lastPrimary, locked, movement, dblThreshold } = this;
    if (locked) {
      movement.copy(lastMovement);
      lastMovement.set(0, 0);
    } else {
      movement.subVectors(current, lastMovement);
      lastMovement.copy(current);
    }
    let primaryDblUp = false;
    if (this.primaryUp) {
      const now = Date.now();
      primaryDblUp = lastPrimary > now - dblThreshold;
      this.lastPrimary = now;
    }
    const frame: PointerFrame = {
      capture: this.onCapture,
      primary: this.primary,
      primaryDown: this.primaryDown,
      primaryPressed: this.primaryPressed,
      primaryDrag: this.primaryDrag,
      primaryDragDown: this.primaryDragDown,
      primaryDoubleDown: this.primaryDoubleDown,
      primaryUp: this.primaryUp,
      primaryDblUp,
      secondary: this.secondary,
      secondaryDown: this.secondaryDown,
      secondaryDrag: this.secondaryDrag,
      secondaryDragDown: this.secondaryDragDown,
      secondaryPressed: this.secondaryPressed,
      secondaryUp: this.secondaryUp,
      tertiary: this.tertiary,
      tertiaryDown: this.tertiaryDown,
      tertiaryDrag: this.tertiaryDrag,
      tertiaryPressed: this.tertiaryPressed,
      tertiaryDragDown: this.tertiaryDragDown,
      tertiaryUp: this.tertiaryUp,
      hover: this.hover,
      locked: this.locked,
      current: this.current,
      movement: this.movement,
      normalized: this.normalized,
      wheel: this.wheel,
      window: this.window,
      wheelEvent: this.wheelEvent,
    };
    this.primaryDown = false;
    this.primaryDragDown = false;
    this.primaryDoubleDown = false;
    this.primaryUp = false;
    this.secondaryDown = false;
    this.secondaryDragDown = false;
    this.secondaryUp = false;
    this.tertiaryDown = false;
    this.tertiaryDragDown = false;
    this.tertiaryUp = false;
    this.wheel = 0;
    this.wheelEvent = undefined;
    return frame;
  }
}
